import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PrivateMessages.css';
import { getRankMeta } from '../utils/rankConfig';
import { getUsers } from '../utils/storage';

function PrivateMessages({ currentUser, onViewProfile }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [imageAttachment, setImageAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Used to auto-refresh messages for correct "DM" experience
  const conversationWatcherRef = useRef();

  // Fetch and display conversation summaries
  const loadConversations = useCallback(() => {
    if (!currentUser) return;
    let allMessages;
    try {
      allMessages = JSON.parse(localStorage.getItem('nitroPrivateMessages') || '[]');
      if (!Array.isArray(allMessages)) allMessages = [];
    } catch (err) {
      allMessages = [];
    }
    // Filter only user's direct messages
    const userMessages = allMessages.filter(
      msg => msg.fromUserId === currentUser.id || msg.toUserId === currentUser.id
    );
    // For each conversation, use the latest message
    const convMap = {};
    userMessages.forEach(msg => {
      const otherUserId = msg.fromUserId === currentUser.id ? msg.toUserId : msg.fromUserId;
      // Sometimes username doesn't get saved; fallback to loading from users list when displaying
      if (!convMap[otherUserId] || new Date(msg.timestamp) > new Date(convMap[otherUserId].lastMessageTime || 0)) {
        convMap[otherUserId] = {
          userId: otherUserId,
          username: msg.fromUserId === currentUser.id ? msg.toUsername : msg.fromUsername,
          lastMessage: msg.image ? '📷 Image' : msg.message,
          lastMessageIsImage: !!msg.image,
          lastMessageTime: msg.timestamp,
          unread: msg.toUserId === currentUser.id && !msg.read,
        };
      } else {
        // Count unread
        if (msg.toUserId === currentUser.id && !msg.read) {
          convMap[otherUserId].unread = true;
        }
      }
    });
    // Fill in any missing usernames from user list
    const usersMap = {};
    getUsers().forEach(u => {
      usersMap[u.id] = u.discordUsername;
    });
    Object.values(convMap).forEach(conv => {
      if (!conv.username && usersMap[conv.userId]) {
        conv.username = usersMap[conv.userId];
      }
    });
    // Sort by lastMessageTime desc
    const list = Object.values(convMap).sort((a, b) =>
      new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
    );
    setConversations(list);
  }, [currentUser]);

  // Load every message between currentUser and the selected conversation partner
  const loadMessages = useCallback(conv => {
    if (!currentUser || !conv) return;
    let allMessages;
    try {
      allMessages = JSON.parse(localStorage.getItem('nitroPrivateMessages') || '[]');
      if (!Array.isArray(allMessages)) allMessages = [];
    } catch {
      allMessages = [];
    }
    // Only messages between these two people
    const filteredMsgs = allMessages.filter(
      msg =>
        (msg.fromUserId === currentUser.id && msg.toUserId === conv.userId) ||
        (msg.fromUserId === conv.userId && msg.toUserId === currentUser.id)
    ).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    // Mark as read any inbound messages
    let updated = false;
    const markedAsRead = allMessages.map(msg => {
      if (
        msg.toUserId === currentUser.id &&
        msg.fromUserId === conv.userId &&
        !msg.read
      ) {
        updated = true;
        return { ...msg, read: true };
      }
      return msg;
    });

    if (updated) {
      try {
        localStorage.setItem('nitroPrivateMessages', JSON.stringify(markedAsRead));
      } catch (err) {
        /* ignore storage error */
      }
    }

    setMessages(filteredMsgs);
    // Ensure conversation list's unread status is updated
    loadConversations();
  }, [currentUser, loadConversations]);

  // Handles real-time updates by polling
  useEffect(() => {
    loadConversations();
  }, [currentUser, loadConversations]);

  // Watch and auto-refresh messages when viewing a conversation
  useEffect(() => {
    if (!selectedConversation) return;
    loadMessages(selectedConversation);
    if (conversationWatcherRef.current) clearInterval(conversationWatcherRef.current);
    const id = setInterval(() => {
      loadMessages(selectedConversation);
    }, 2000);
    conversationWatcherRef.current = id;
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [selectedConversation, currentUser, loadMessages]);

  // Keep conversation list up-to-date every few seconds
  useEffect(() => {
    const id = setInterval(() => {
      loadConversations();
    }, 3000);
    return () => clearInterval(id);
  }, [currentUser, loadConversations]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const rankMeta = currentUser ? getRankMeta(currentUser.role) : getRankMeta();
  const permissions = rankMeta.permissions || {};
  const canSendPrivateImages = !('canSendPrivateImages' in permissions) || permissions.canSendPrivateImages;

  // Sending messages
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!currentUser || !selectedConversation) return;
    if (!newMessage.trim() && !imageAttachment) return; // Don't send empty
    if (imageAttachment && !canSendPrivateImages) {
      setAttachmentError('Your rank cannot send images in private chats.');
      return;
    }

    // Pull target user's full profile for robust username (in case username changed)
    const userList = getUsers();
    const toUser = userList.find(u => u.id === selectedConversation.userId);
    const toUsername = toUser ? toUser.discordUsername : selectedConversation.username;
    
    const fromUsername = currentUser.discordUsername;
    const now = new Date();
    const messageObj = {
      id: `${Date.now()}-${Math.floor(Math.random()*1000000)}`,
      fromUserId: currentUser.id,
      fromUsername,
      toUserId: selectedConversation.userId,
      toUsername,
      message: newMessage.trim(),
      image: imageAttachment?.dataUrl || null,
      timestamp: now.toISOString(),
      read: false
    };
    let allMsgs;
    try {
      allMsgs = JSON.parse(localStorage.getItem('nitroPrivateMessages') || '[]');
      if (!Array.isArray(allMsgs)) allMsgs = [];
    } catch {
      allMsgs = [];
    }
    allMsgs.push(messageObj);
    try {
      localStorage.setItem('nitroPrivateMessages', JSON.stringify(allMsgs));
    } catch (err) {
      alert('Error saving message.');
    }
    setNewMessage('');
    setImageAttachment(null);
    setAttachmentError('');
    loadMessages(selectedConversation);
    loadConversations();
  };

  // Start message thread with selected user. Can search by username or email.
  const startNewConversation = useCallback(() => {
    if (!searchUser.trim()) return;
    const users = (getUsers() || []);
    const lowerSearch = searchUser.trim().toLowerCase();
    // Find by username first, or email
    const otherUser = users.find(u =>
      (u.discordUsername && u.discordUsername.toLowerCase() === lowerSearch) ||
      (u.email && u.email.toLowerCase() === lowerSearch)
    );
    if (!otherUser) {
      alert('User not found');
      return;
    }
    if (otherUser.id === currentUser.id) {
      alert('You cannot message yourself');
      return;
    }
    setSelectedConversation({
      userId: otherUser.id,
      username: otherUser.discordUsername
    });
    setSearchUser('');
  }, [searchUser, currentUser]);

  // File/image attachment handlers
  const handleAttachmentChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAttachmentError('Only image files are allowed.');
      event.target.value = '';
      return;
    }
    if (file.size > 1024 * 1024) {
      setAttachmentError('Image must be under 1MB.');
      event.target.value = '';
      return;
    }
    const reader = new window.FileReader();
    reader.onload = () => {
      setImageAttachment({
        name: file.name,
        dataUrl: reader.result
      });
      setAttachmentError('');
    };
    reader.onerror = () => setAttachmentError('Error reading file.');
    reader.readAsDataURL(file);
  };

  const removeAttachment = () => {
    setImageAttachment(null);
    setAttachmentError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Show "just now" etc
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'just now';
    if (diff < 60*60*1000) return `${Math.floor(diff/60000)}m ago`;
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Robust loading of username (in case missing, fallback to id)
  const getUsernameFromUserId = (userId) => {
    const users = getUsers();
    const user = users.find(u => u.id === userId);
    return user ? user.discordUsername : userId;
  };

  if (!currentUser) {
    return (
      <div className="messages-container">
        <div className="messages-login-required">
          <h2>Private Messages</h2>
          <p>Please log in to view and send private messages.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="messages-container">
      <div className="messages-sidebar">
        <div className="messages-header">
          <h2>💬 Messages</h2>
          <div className="new-conversation">
            <input
              type="text"
              placeholder="Search user (username or email)..."
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); startNewConversation(); }
              }}
            />
            <button onClick={startNewConversation}>New</button>
          </div>
        </div>
        <div className="conversations-list">
          {conversations.length === 0 ? (
            <div className="no-conversations">
              <p>No conversations yet. Start a new conversation above!</p>
            </div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.userId}
                className={`conversation-item ${selectedConversation?.userId === conv.userId ? 'active' : ''} ${conv.unread ? 'unread' : ''}`}
                onClick={() => setSelectedConversation(conv)}
              >
                <div
                  className="conversation-avatar"
                  onClick={e => {
                    e.stopPropagation();
                    if (onViewProfile) onViewProfile(conv.userId);
                  }}
                  style={{ cursor: onViewProfile ? 'pointer' : 'default' }}
                  title={conv.username || getUsernameFromUserId(conv.userId)}
                >
                  {(conv.username || getUsernameFromUserId(conv.userId) || '').charAt(0).toUpperCase()}
                </div>
                <div className="conversation-info">
                  <div className="conversation-header">
                    <strong
                      onClick={e => {
                        e.stopPropagation();
                        if (onViewProfile) onViewProfile(conv.userId);
                      }}
                      style={{ cursor: onViewProfile ? 'pointer' : 'default' }}
                    >
                      {conv.username || getUsernameFromUserId(conv.userId)}
                    </strong>
                    <span className="conversation-time">{formatTime(conv.lastMessageTime)}</span>
                  </div>
                  <p className="conversation-preview">
                    {conv.lastMessageIsImage ? '📷 Image' : conv.lastMessage}
                  </p>
                </div>
                {conv.unread && <div className="unread-indicator" aria-label="unread" />}
              </div>
            ))
          )}
        </div>
      </div>
      <div className="messages-content">
        {selectedConversation ? (
          <>
            <div className="messages-chat-header">
              <h3
                onClick={() => onViewProfile && onViewProfile(selectedConversation.userId)}
                style={{ cursor: onViewProfile ? 'pointer' : 'default' }}
              >
                {selectedConversation.username || getUsernameFromUserId(selectedConversation.userId)}
              </h3>
            </div>
            <div className="messages-chat" style={{ overflowY: 'auto', flex: '1 1 0' }}>
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`message-bubble ${msg.fromUserId === currentUser.id ? 'sent' : 'received'}`}
                >
                  {msg.message && <div className="message-text">{msg.message}</div>}
                  {msg.image && (
                    <div className="message-image">
                      <img src={msg.image} alt="Attachment" loading="lazy" style={{ maxWidth: 350, maxHeight: 200, borderRadius: 8 }} />
                    </div>
                  )}
                  <div className="message-time">{formatTime(msg.timestamp)}</div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <form className="messages-input-form" onSubmit={handleSendMessage}>
              <div className="messages-input-column">
                <input
                  type="text"
                  className="messages-input"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  maxLength="1000"
                  autoFocus
                />
                <div className="messages-attachment-row">
                  <button
                    type="button"
                    className="messages-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!canSendPrivateImages}
                    title={canSendPrivateImages ? 'Attach image' : 'Your rank cannot send images'}
                  >
                    📎 Attach Image
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/*"
                    onChange={handleAttachmentChange}
                  />
                  {imageAttachment && (
                    <div className="messages-attachment-preview">
                      <span>{imageAttachment.name}</span>
                      <button type="button" onClick={removeAttachment}>Remove</button>
                    </div>
                  )}
                </div>
                {attachmentError && <p className="messages-attachment-error">{attachmentError}</p>}
              </div>
              <button
                type="submit"
                className="messages-send-btn"
                disabled={(!newMessage.trim() && !imageAttachment)}
              >
                Send
              </button>
            </form>
          </>
        ) : (
          <div className="messages-placeholder">
            <h3>Select a conversation to start messaging</h3>
            <p>Or start a new conversation using the search above</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PrivateMessages;
